import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import ReplyAsSelector from '../inbox/ReplyAsSelector';
import ProviderBadge from '../inbox/ProviderBadge';
import { useSendEmail } from '../../hooks/useInbox';
import { usePersonalSenders } from '../../hooks/usePersonalSenders';
import { buildContactMergeMap, resolveMergeVars } from '../../lib/mergeVars';
import { logEventEmail, newOperationId } from '../../lib/eventOutreachActions';
import { errorText } from '../../lib/apiErrors';

const escapeHtml = text => String(text || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const textToHtml = text => `<p>${escapeHtml(text).replace(/\n{2,}/g, '</p><p>').replace(/\n/g, '<br/>')}</p>`;

/**
 * Bulk "Send invite" from the Outreach board (#894): ONE email per selected contact through the
 * user's connected mailbox (the same send lane as Contact Detail's composer), merge fields resolved
 * per contact, then each send logged on the event with one shared operation id — so each contact's
 * history entry carries the same id, the stage moves to Invited and one follow-up task is created.
 *
 * The Guests tab (#1013) reuses it for every email it sends from a card or a bar, by prefilling it:
 * `initialSubject` / `initialBody` (an invite, a reminder, a nudge, a thank-you, the new-date email after a
 * reschedule), `noun` ("invite", "reminder"…) for the title and button, and `doneVerb` for the toast
 * ("Invited", "Reminded"). The toast says it only after the send succeeded (P7). Omitted, it behaves as before.
 */
export default function BulkEventInviteDialog(props) {
    // Mounted only while open: the body reads the mailbox status, which no closed dialog should fetch.
    return props.open ? <BulkEventInviteBody {...props} /> : null;
}

function BulkEventInviteBody({
    open, onClose, eventId, eventName, contacts = [], getAuthHeader, onDone,
    initialSubject, initialBody = '', noun = 'invite', doneVerb = 'Invited',
}) {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [sending, setSending] = useState(false);
    const { options, provider, selectByOptionValue, sendFlags, isLoading: sendersLoading, hasAnySender } = usePersonalSenders();
    const sendEmail = useSendEmail();
    useEffect(() => {
        if (open) { setSubject(initialSubject ?? (eventName ? `You're invited: ${eventName}` : '')); setBody(initialBody || ''); }
    }, [open, eventName, initialSubject, initialBody]);
    const withEmail = contacts.filter(contact => contact.email);

    const send = async () => {
        if (!subject.trim() || !body.trim()) { toast.error('Subject and message are required'); return; }
        setSending(true);
        const operationId = newOperationId('bulk-invite', eventId);
        const results = [];
        for (const contact of contacts) {
            if (!contact.email) { results.push({ contact, ok: false, message: 'no email address' }); continue; }
            const merge = buildContactMergeMap(contact);
            const contactSubject = resolveMergeVars(subject, merge);
            try {
                await sendEmail.mutateAsync({
                    to: contact.email, subject: contactSubject, body: textToHtml(resolveMergeVars(body, merge)),
                    provider, ...sendFlags, contact_id: contact.contact_id,
                });
                await logEventEmail({ getAuthHeader, eventId, contactId: contact.contact_id, subject: contactSubject, operationId });
                results.push({ contact, ok: true });
            } catch (err) {
                results.push({ contact, ok: false, message: errorText(err, noun === 'invite' ? 'could not be invited' : `${noun} could not be sent`) });
            }
        }
        setSending(false);
        const failed = results.filter(result => !result.ok);
        if (failed.length) {
            toast.warning(`${doneVerb} ${results.length - failed.length} of ${results.length}`);
            failed.forEach(({ contact, message }) => toast.error(`${contact.name || contact.email || contact.contact_id}: ${message}`));
        } else {
            toast.success(`${doneVerb} ${results.length} contact${results.length === 1 ? '' : 's'}`);
        }
        await onDone?.({ operationId, results });
    };

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next && !sending) onClose?.(); }}>
            <DialogContent data-testid="bulk-invite-dialog" style={{ width: '560px', maxWidth: '95vw' }}>
                <DialogHeader><DialogTitle>Send {noun} to {contacts.length} contact{contacts.length === 1 ? '' : 's'}</DialogTitle></DialogHeader>
                <ReplyAsSelector
                    label="From"
                    options={options}
                    value={provider}
                    onChange={selectByOptionValue}
                    singleHint="This is the only mailbox you have connected."
                    emptyHint={sendersLoading ? 'Checking connected mailboxes…' : 'No mailbox connected'}
                    renderBadge={(opt) => <ProviderBadge provider={opt.provider} />}
                />
                {!sendersLoading && !hasAnySender && <p className="text-xs text-amber-700">No email provider connected — each invite will only be logged in the CRM.</p>}
                <p className="text-xs text-stone-500">One email per contact. {withEmail.length < contacts.length && `${contacts.length - withEmail.length} without an email address will be skipped.`}</p>
                <label className="block text-xs font-medium text-stone-600">Subject
                    <input aria-label="Invite subject" value={subject} onChange={event => setSubject(event.target.value)} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm" />
                </label>
                <label className="block text-xs font-medium text-stone-600">Message
                    <textarea aria-label="Invite message" value={body} onChange={event => setBody(event.target.value)} rows={7} className="mt-1 w-full rounded border border-stone-200 px-2 py-2 text-sm" placeholder={'Hi {{FirstName}},\n\n…'} />
                </label>
                <p className="text-[11px] text-stone-500">{'{{FirstName}}'}, {'{{LastName}}'} and {'{{Title}}'} are filled in for each contact.</p>
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
                    <Button onClick={send} disabled={sending || withEmail.length === 0}>{sending ? 'Sending…' : `Send ${withEmail.length} ${noun}${withEmail.length === 1 ? '' : 's'}`}</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
